import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import {
  ArrowRight, MapPin, Phone, Star, Flame, Leaf, Instagram,
  Sparkles, Award, Heart, Truck, ChevronRight, Egg,
} from "lucide-react";
import Reveal from "../components/Reveal";
import MenuCard from "../components/MenuCard";
import MarqueeBand from "../components/MarqueeBand";
import TestimonialSlider from "../components/TestimonialSlider";
import { HERO_IMAGES, TRUCK_IMAGE } from "../data/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Home() {
  const [bestSellers, setBestSellers] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    axios.get(`${API}/best-sellers`).then((r) => setBestSellers(r.data.items || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] overflow-hidden flex items-end pb-16 md:pb-24">
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img
            src={HERO_IMAGES[0]}
            alt="Indian egg curry close up"
            className="w-full h-full object-cover scale-110"
          />
          <div className="absolute inset-0 cinema-overlay" />
        </motion.div>

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-32 md:pt-40">
          <Reveal delay={0.08}>
            <h1 className="mt-5 text-[2.65rem] sm:text-6xl md:text-7xl lg:text-8xl font-extrabold leading-[0.92] balanced text-white">
              Melbourne's Favourite{" "}
              <span className="font-script text-yellow-brand text-glow-yellow text-6xl sm:text-7xl md:text-8xl lg:text-9xl">
                Egg Flavours
              </span>{" "}
              <span className="block">— Now in Clayton.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 text-base md:text-xl text-white/75 max-w-2xl leading-relaxed">
              30+ flavour-packed dishes straight from the streets of India. Slow-cooked masalas, runny yolks, smoky tadka — served fresh from our truck.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/menu" className="btn-primary" data-testid="hero-view-menu">
                View Menu <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="hero-visit-truck">
                <MapPin className="w-4 h-4" /> Visit Food Truck
              </Link>
              <a href="tel:0434508712" className="btn-outline" data-testid="hero-order-now">
                <Phone className="w-4 h-4" /> Order Now
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <div className="mt-12 flex items-center gap-5 text-white/70 text-sm">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-brand" fill="#F4BB29" />
                ))}
              </div>
              <span className="font-semibold text-white">5.0 · Loved in Clayton</span>
            </div>
          </Reveal>
        </motion.div>

        {/* Floating yolk */}
        <div className="hidden md:block absolute right-12 top-32 w-24 h-24 rounded-full bg-yellow-brand bob blur-[1px] opacity-90" />
        <div className="hidden md:block absolute right-20 top-40 w-10 h-10 rounded-full bg-yellow-brand/70 yolk-pulse" />
      </section>

      <MarqueeBand />

      {/* SIGNATURE DISHES (BEST SELLERS) */}
      <section className="py-20 md:py-32 max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <Reveal><span className="eyebrow">The Crowd Favourites</span></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-3 text-4xl md:text-6xl font-extrabold leading-tight">
                Signature{" "}
                <span className="font-script text-yellow-brand text-5xl md:text-7xl">Dishes</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.16}>
            <Link to="/menu" className="text-yellow-brand font-bold text-sm uppercase tracking-widest inline-flex items-center gap-1 hover:gap-3 transition-all">
              See full menu <ChevronRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {bestSellers.slice(0, 6).map((item, i) => (
            <Reveal key={item.id} delay={i * 0.06}>
              <MenuCard item={item} featured={i === 0} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY CUSTOMERS LOVE US */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-transparent via-yellow-brand/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Reveal><span className="eyebrow">Why customers love us</span></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-3 text-4xl md:text-6xl font-extrabold leading-tight balanced">
                Big flavour. <span className="font-script text-yellow-brand text-5xl md:text-7xl">No shortcuts.</span>
              </h2>
            </Reveal>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Sparkles />, title: "Authentic recipes", body: "Slow-built masalas the way our grandmothers made them — no compromises." },
              { icon: <Heart />,    title: "Cooked fresh", body: "Every dish made to order. Hot, smoky, runny-yolk perfect." },
              { icon: <Award />,    title: "30+ dishes", body: "From street-style bhurji to our signature Tiranga — there's a flavour for everyone." },
              { icon: <Truck />,    title: "Khau Gali, Clayton", body: "Find us in Khau Gali, Clayton. Follow @tasteofegg_australia." },
            ].map((b, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="card-dark p-7 h-full">
                  <div className="w-12 h-12 rounded-full bg-yellow-brand/15 text-yellow-brand flex items-center justify-center mb-5">
                    {b.icon}
                  </div>
                  <h3 className="text-xl font-extrabold">{b.title}</h3>
                  <p className="text-white/60 mt-2 leading-relaxed text-sm">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-20 md:py-32 max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
          <Reveal>
            <div className="hover-zoom relative aspect-[4/5] rounded-3xl overflow-hidden">
              <img src={TRUCK_IMAGE} alt="Taste of Egg food truck Clayton Melbourne" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <span className="font-script text-yellow-brand text-4xl md:text-5xl text-glow-yellow">
                  Made with love
                </span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <span className="eyebrow">Our story</span>
            <h2 className="mt-3 text-4xl md:text-6xl font-extrabold leading-tight">
              From Indian streets <span className="font-script text-yellow-brand text-5xl md:text-7xl">to Clayton.</span>
            </h2>
            <p className="text-white/65 mt-6 text-lg leading-relaxed">
              Taste of Egg was born from a simple idea — bring the smoky, late-night egg flavours of Indian street corners to Melbourne. No fancy menus. No fluff. Just generations-old masala, sizzling tadka, and the kind of bhurji you remember.
            </p>
            <p className="text-white/65 mt-4 text-lg leading-relaxed">
              We crack over a thousand eggs a week. And we love every single one of them.
            </p>
            <Link to="/about" className="btn-outline mt-8" data-testid="about-cta">
              Read our story <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-32 bg-[#0c0c0c] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <span className="eyebrow">Word on the truck</span>
              <h2 className="mt-3 text-4xl md:text-6xl font-extrabold leading-tight">
                Real <span className="font-script text-yellow-brand text-5xl md:text-7xl">cravings.</span>
              </h2>
            </div>
          </Reveal>
          <TestimonialSlider />
        </div>
      </section>

      {/* INSTAGRAM-STYLE TILES (decorative, no dish photos until uploaded) */}
      <section className="py-20 md:py-32 max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <Reveal><span className="eyebrow">@tasteofegg_australia</span></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-3 text-4xl md:text-6xl font-extrabold leading-tight">
                Straight from{" "}
                <span className="font-script text-yellow-brand text-5xl md:text-7xl">the truck</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.16}>
            <a
              href="https://instagram.com/tasteofegg_australia"
              target="_blank"
              rel="noreferrer"
              className="btn-outline"
              data-testid="ig-cta"
            >
              <Instagram className="w-4 h-4" /> Follow on Instagram
            </a>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { kind: "truck" },
            { kind: "tag", text: "Egg Bhurji", sub: "Mumbai street style" },
            { kind: "tag", text: "Tiranga", sub: "Three flavours, one plate" },
            { kind: "tag", text: "Surti Gotalo", sub: "Smoky · soulful · spicy" },
            { kind: "tag", text: "Cheese Bhurji", sub: "Molten goodness" },
            { kind: "tag", text: "Half Fry", sub: "Runny yolk magic" },
            { kind: "tag", text: "Anda Pulav", sub: "Fragrant basmati" },
            { kind: "tag", text: "Cuttlesh", sub: "Onion · chilli · coriander" },
          ].map((tile, i) => (
            <Reveal key={i} delay={i * 0.04}>
              {tile.kind === "truck" ? (
                <Link to="/gallery" className={`hover-zoom block aspect-square rounded-2xl overflow-hidden md:row-span-2 md:aspect-[1/2]`}>
                  <img src={TRUCK_IMAGE} alt="Taste of Egg food truck" loading="lazy" className="w-full h-full object-cover" />
                </Link>
              ) : (
                <Link
                  to="/menu"
                  className="block aspect-square rounded-2xl overflow-hidden card-dark p-5 group transition-all duration-500 hover:-translate-y-1.5"
                >
                  <div className="h-full w-full flex flex-col justify-between">
                    <div className="w-10 h-10 rounded-full bg-yellow-brand/15 text-yellow-brand flex items-center justify-center group-hover:bg-yellow-brand group-hover:text-black transition-colors">
                      <Egg className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="font-script text-yellow-brand text-3xl md:text-4xl leading-none">{tile.text}</div>
                      <div className="text-white/55 text-xs mt-2 uppercase tracking-widest">{tile.sub}</div>
                    </div>
                  </div>
                </Link>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      {/* LOCATION / MAP */}
      <section className="py-20 md:py-32 max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <span className="eyebrow">Find the truck</span>
            <h2 className="mt-3 text-4xl md:text-6xl font-extrabold leading-tight">
              Find us at <span className="font-script text-yellow-brand text-5xl md:text-7xl">Khau Gali, Clayton.</span>
            </h2>
            <p className="text-white/85 mt-6 text-lg leading-relaxed font-semibold">
              Khau gali, Clayton South, VIC 3169
            </p>
            <p className="text-white/55 mt-2 leading-relaxed">
              One truck. One spot. Drop in or call ahead — we'll have your order sizzling.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="tel:0434508712" className="btn-primary" data-testid="map-call-cta">
                <Phone className="w-4 h-4" /> 0434 508 712
              </a>
              <a
                href="https://maps.app.goo.gl/7Crt1GqZc94ddNjz9"
                target="_blank"
                rel="noreferrer"
                className="btn-outline"
                data-testid="get-directions"
              >
                <MapPin className="w-4 h-4" /> Get directions
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-3xl overflow-hidden border border-white/10 aspect-[4/3] glow-yellow">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3146.8878246618337!2d145.11753237657584!3d-37.933050971945896!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad66bdeb895d3c5%3A0xb054842e6cb07e04!2sTaste%20of%20Egg!5e0!3m2!1sen!2sau!4v1788775309990!5m2!1sen!2sau"
                className="w-full h-full"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                title=  "Taste of Egg location"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="relative rounded-3xl overflow-hidden p-10 md:p-20 bg-yellow-brand text-black">
            <div className="absolute -right-16 -bottom-20 w-72 h-72 rounded-full bg-black/15 blur-2xl" />
            <div className="relative">
              <span className="uppercase text-xs tracking-[0.25em] font-extrabold">Hungry already?</span>
              <h2 className="mt-3 text-4xl md:text-6xl font-extrabold leading-tight balanced">
                Visit the truck today.{" "}
                <span className="font-script text-6xl md:text-8xl">Bring your hunger.</span>
              </h2>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="tel:0434508712" className="bg-black text-yellow-brand font-extrabold rounded-full px-7 py-3 inline-flex items-center gap-2 hover:bg-black/85 transition-colors" data-testid="banner-call">
                  <Phone className="w-4 h-4" /> Call 0434 508 712
                </a>
                <Link to="/menu" className="bg-white/15 text-black border border-black/20 font-extrabold rounded-full px-7 py-3 inline-flex items-center gap-2 hover:bg-white/30 transition-colors">
                  View Menu <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
